import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { parseURLParams } from '@offerzen/pinata-shame'
import browserHistory from '@/core/config/browserHistory'

import { updateQueryParams } from '@/core/helpers/historyHelpers'

const calculateOffset = (page, limit) => limit * (page - 1)

const isValidPageParam = pageNumber => {
  return pageNumber > 0 && Number.isInteger(pageNumber)
}

/**
 * @typedef {Object} PaginationState
 * @property {number} currentPage - The current page number.
 * @property {(pageNumber: number) => void}  setPage - Sets the current page number.
 * @property {number} offset - The offset for the current page.
 * @property {(totalCount: number) => number} calculateTotalPages - Calculates the total number of pages based on the total count.
 */

/**
 * Custom hook for handling pagination logic, using the URL query params.
 *
 * @param {Object} options - The pagination options.
 * @param {number} options.limit - The number of items per page.
 * @returns {PaginationState} - The pagination state, actions, and helpers.
 */
export const usePagination = ({ limit }) => {
  const { search } = useLocation()
  const { page: pageParam } = parseURLParams(search)

  const setPage = useCallback(
    pageNumber => {
      browserHistory.replace({ search: updateQueryParams(search, { page: pageNumber }) })
    },
    [search],
  )

  const pageNumber = Number(pageParam)

  useEffect(() => {
    if (isValidPageParam(pageNumber)) return

    setPage(1)
  }, [pageNumber, setPage])

  const currentPage = isValidPageParam(pageNumber) ? pageNumber : 1

  return {
    currentPage,
    setPage,
    offset: calculateOffset(currentPage, limit),
    calculateTotalPages: totalCount => Math.ceil(totalCount / limit),
  }
}
